import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  Subject,
  catchError,
  debounceTime,
  filter,
  forkJoin,
  map,
  merge,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { HttpService } from '../../services/http.service';

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  type: 'student' | 'school' | 'event' | 'teacher' | 'user' | 'csq_team';
  url?: any;
  data?: any;
}

interface GroupedResults {
  users: SearchResult[];
  schools: SearchResult[];
  events: SearchResult[];
  csq_team: SearchResult[];
  student: SearchResult[];
}

@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss'],
})
export class GlobalSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  isOpen = false;
  isSearching = false;
  noResults = false;
  searchText = '';

  userRole: any = localStorage.getItem('userRole');

  recentSearches: string[] = [];

  // groupedResults: GroupedResults = {
  //     users: [],
  //     schools: [],
  //     events: [],
  //     csq_team: [],
  //     student: []
  // };

  expandedSections = {
    users: true,
    schools: true,
    events: true,
    csq_team: true,
    student: true,
  };

  groupedResults: any = {};

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  private readonly RECENT_SEARCHES_KEY = 'global_search_recent_searches';
  private readonly MAX_RECENT_SEARCHES = 10;
  totalApis = 0;
  completedApis = 0;

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  constructor(
    private httpService: HttpService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadRecentSearches();

    this.searchSubject
      .pipe(
        debounceTime(300),
        map((v) => v.trim()),
        filter((v) => v.length >= 2),
        tap(() => {
          this.isSearching = true;
          this.noResults = false;
        }),
        switchMap((search) => this.searchApis(search)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res) => {
          this.isSearching = false;

          if (!res) return;
          // console.log(res,"res res");
          this.updateResultsDynamically(res.type, res.data);
          this.completedApis++;
        },
        error: (error) => {
          this.isSearching = false;
          this.noResults = true;
          console.error('Search error:', error);
        },
      });
    // .subscribe({
    //     next: (res) => {
    //         this.isSearching = false;
    //         if (!res) {
    //             this.noResults = true;
    //             return;
    //         }
    //         this.processCombinedResults(res);
    //     },
    //     error: (error) => {
    //         this.isSearching = false;
    //         this.noResults = true;
    //         console.error('Search error:', error);
    //     }
    // });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.clearSearch();
      });
  }

  ngAfterViewInit(): void {
    if (this.searchInput && this.searchInput.nativeElement && this.isOpen) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      }, 100);
    }
  }

  getUserRole(userData: any): string {
    if (!userData) return 'User';
    if (userData.userRole) {
      return userData.userRole
        .split('_')
        .map(
          (word: string) =>
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ');
    }
    return 'User';
  }

  getSchoolLocation(schoolData: any): string {
    if (!schoolData) return '';

    const locationParts = [];
    if (schoolData.city) locationParts.push(schoolData.city);
    if (schoolData.state) locationParts.push(schoolData.state);
    if (schoolData.country) locationParts.push(schoolData.country);
    if (schoolData.location) locationParts.push(schoolData.location);

    if (locationParts.length === 0) {
      return 'No location specified';
    }

    return locationParts.join(', ');
  }

  getStudentGrade(studentData: any): string {
    if (!studentData) return 'Student';

    if (studentData.gradeLevel) {
      return `Grade ${studentData.gradeLevel} Student`;
    }
    return 'Student';
  }

  getStudentSchool(studentData: any): string {
    if (!studentData) return '';

    if (studentData.schoolName) {
      return studentData.schoolName;
    }

    if (studentData.school && studentData.school.schoolName) {
      return studentData.school.schoolName;
    }

    return 'No school assigned';
  }

  getEventDetails(eventData: any): string {
    if (!eventData) return '';

    const details = [];

    if (eventData.eventDate) {
      const date = new Date(eventData.eventDate);
      details.push(date.toLocaleDateString());
    }

    if (eventData.location) {
      details.push(eventData.location);
    }

    if (eventData.eventType) {
      details.push(eventData.eventType);
    }

    return details.join(' • ');
  }

  // private searchApis(search: string) {
  //     const searchBody = { search };

  //     if (this.userRole === 'teacher') {
  //         return forkJoin({
  //             events: this.safePost('p_global_search_/teacher/event/search', searchBody),
  //             adminevents: this.safePost('p_global_search_/teacher/adminevent/search', searchBody),
  //             students: this.safePost('p_global_search_/teacher/student/search', searchBody)
  //         }).pipe(
  //             catchError((error) => {
  //                 console.error('Teacher search error:', error);
  //                 return of(null);
  //             })
  //         );
  //     } else {
  //         return forkJoin({
  //             users: this.safePost('p_global_search_/user/search', searchBody),
  //             schools: this.safePost('p_global_search_/school/search', searchBody),
  //             students: this.safePost('p_global_search_/student/search', searchBody),
  //             events: this.safePost('p_global_search_/event/search', searchBody),
  //             csq_team: this.safePost('p_global_search_/csq_team/search', searchBody)
  //         }).pipe(
  //             catchError((error) => {
  //                 console.error('Admin search error:', error);
  //                 return of(null);
  //             })
  //         );
  //     }
  // }

  private searchApis(search: string) {
    const searchBody = { search };

    if (this.userRole === 'teacher') {
      this.totalApis = 3;
      this.completedApis = 0;

      return merge(
        this.safePost('p_global_search_/teacher/event/search', searchBody).pipe(
          map((res) => ({ type: 'events', data: res })),
        ),

        this.safePost(
          'p_global_search_/teacher/adminevent/search',
          searchBody,
        ).pipe(map((res) => ({ type: 'adminevents', data: res }))),

        this.safePost(
          'p_global_search_/teacher/student/search',
          searchBody,
        ).pipe(map((res) => ({ type: 'students', data: res }))),
      );
    } else {
      this.totalApis = 5;
      this.completedApis = 0;

      return merge(
        this.safePost('p_global_search_/user/search', searchBody).pipe(
          map((res) => ({ type: 'users', data: res })),
        ),

        this.safePost('p_global_search_/school/search', searchBody).pipe(
          map((res) => ({ type: 'schools', data: res })),
        ),

        this.safePost('p_global_search_/student/search', searchBody).pipe(
          map((res) => ({ type: 'students', data: res })),
        ),

        this.safePost('p_global_search_/event/search', searchBody).pipe(
          map((res) => ({ type: 'events', data: res })),
        ),

        this.safePost('p_global_search_/csq_team/search', searchBody).pipe(
          map((res) => ({ type: 'csq_team', data: res })),
        ),
      );
    }
  }

  private safePost(url: string, body: any) {
    return this.httpService.post(url, body).pipe(
      catchError((error) => {
        console.error(`API call failed for ${url}:`, error);
        return of({ data: [] });
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRecentSearches(): void {
    const stored = localStorage.getItem(this.RECENT_SEARCHES_KEY);
    if (stored) {
      try {
        this.recentSearches = JSON.parse(stored);
      } catch (e) {
        this.recentSearches = [];
      }
    }
  }

  private saveRecentSearches(): void {
    localStorage.setItem(
      this.RECENT_SEARCHES_KEY,
      JSON.stringify(this.recentSearches),
    );
  }

  private addToRecentSearches(searchTerm: string): void {
    if (!searchTerm.trim()) return;

    const index = this.recentSearches.indexOf(searchTerm);
    if (index > -1) {
      this.recentSearches.splice(index, 1);
    }

    this.recentSearches.unshift(searchTerm);

    if (this.recentSearches.length > this.MAX_RECENT_SEARCHES) {
      this.recentSearches = this.recentSearches.slice(
        0,
        this.MAX_RECENT_SEARCHES,
      );
    }

    this.saveRecentSearches();
  }

  removeRecentSearch(searchTerm: string): void {
    const index = this.recentSearches.indexOf(searchTerm);
    if (index > -1) {
      this.recentSearches.splice(index, 1);
      this.saveRecentSearches();
    }
  }

  clearRecentSearches(): void {
    this.recentSearches = [];
    this.saveRecentSearches();
  }

  selectRecentSearch(searchTerm: string): void {
    this.searchText = searchTerm;
    this.isOpen = true;
    this.addToRecentSearches(searchTerm);
    this.searchSubject.next(searchTerm);

    setTimeout(() => {
      if (this.searchInput && this.searchInput.nativeElement) {
        this.searchInput.nativeElement.focus();
        this.searchInput.nativeElement.setSelectionRange(
          searchTerm.length,
          searchTerm.length,
        );
      }
    }, 0);
  }

  onSearchInput(event: Event): void {
    const role = localStorage.getItem('userRole');
    if (role == 'student') return;

    const value = (event.target as HTMLInputElement).value;
    this.searchText = value;

    if (!this.isOpen) {
      this.isOpen = true;
    }

    if (!value || value.trim().length < 2) {
      this.resetResults();
      return;
    }

    this.searchSubject.next(value);
  }

  onFocus(): void {
    this.isOpen = true;

    setTimeout(() => {
      if (this.searchInput && this.searchInput.nativeElement) {
        this.searchInput.nativeElement.focus();
      }
    }, 0);
  }

  clearSearch(): void {
    this.searchText = '';
    this.resetResults();
    this.isOpen = false;
  }

  selectResult(result: SearchResult): void {
    if (this.searchText.trim().length >= 2) {
      this.addToRecentSearches(this.searchText.trim());
    }

    this.closeSearch();
    this.clearSearch();

    if (result.type === 'event') {
      const segmentValue = result?.data?.createdby?.toLowerCase();
      this.router.navigate([`${result.url}`], {
        queryParams: { segment: segmentValue },
      });
    } else if (result.type == 'user' || result.type == 'csq_team') {
      this.router.navigate([`${result.url}`], {
        queryParams: { search: result.id },
      });
    } else {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([`${result.url}`]);
      });
    }
  }

  private processCombinedResults(res: any): void {
    const grouped: GroupedResults = {
      users: [],
      schools: [],
      events: [],
      csq_team: [],
      student: [],
    };

    if (this.userRole === 'teacher') {
      (res.students?.data || []).forEach((item: any) => {
        grouped.student.push({
          id: item.studentId || item.id,
          title: `${item.firstName || ''} ${item.lastName || ''}`,
          type: 'student',
          url: `students/view/${item.studentId || item.id}`,
          data: item,
        });
      });

      (res.events?.data || []).forEach((event: any) => {
        grouped.events.push({
          id: event.eventId || event.id,
          title: event.eventName || event.name,
          type: 'event',
          url: `events/event-list/view/${event.eventId || event.id}`,
          data: event,
        });
      });

      (res.adminevents?.data || []).forEach((event: any) => {
        grouped.events.push({
          id: event.eventId || event.id,
          title: event.eventName || event.name,
          type: 'event',
          url: `events/event-list/view/${event.eventId || event.id}`,
          data: event,
        });
      });
    } else {
      (res.users?.data?.users || []).forEach((item: any) => {
        grouped.users.push({
          id: item.id,
          title: `${item.firstName || ''} ${item.lastName || ''}`,
          url: `users`,
          type: 'user',
          data: item,
        });
      });

      (res.students?.data?.student || []).forEach((item: any) => {
        grouped.student.push({
          id: item.studentId,
          title: `${item.firstName || ''} ${item.lastName || ''}`,
          type: 'student',
          url: `students/view/${item.studentId}`,
          data: item,
        });
      });

      (res.schools?.data?.school || []).forEach((school: any) => {
        grouped.schools.push({
          id: school.schoolId,
          title: school.schoolName,
          type: 'school',
          url: `schools/view/${school.schoolId}`,
          data: school,
        });
      });

      (res.events?.data || []).forEach((event: any) => {
        grouped.events.push({
          id: event.eventId,
          title: event.eventName,
          type: 'event',
          url: `events/event-list/view/${event.eventId}`,
          data: event,
        });
      });

      (res.csq_team?.data || []).forEach((item: any) => {
        grouped.csq_team.push({
          id: item.id,
          title: `${item.firstName || ''} ${item.lastName || ''}`,
          type: 'csq_team',
          url: `csq-team`,
          data: item,
        });
      });
    }

    this.groupedResults = grouped;

    this.noResults =
      !grouped.users.length &&
      !grouped.student.length &&
      !grouped.schools.length &&
      !grouped.csq_team.length &&
      !grouped.events.length;
  }

  private updateResultsDynamically(type: string, data: any) {
    let array =
      data?.data?.school ||
      data?.data?.student ||
      data?.data?.users ||
      data?.data;

    if (!array || array.length === 0) {
      this.groupedResults[type == 'students' ? 'student' : type] = [];
      return;
    }
    let newArray: any = [];

    if (this.userRole === 'teacher') {
      if (type == 'students') {
        (array || []).forEach((item: any) => {
          newArray.push({
            id: item.studentId || item.id,
            title: `${item.firstName || ''} ${item.lastName || ''}`,
            type: 'student',
            url: `students/view/${item.studentId || item.id}`,
            data: item,
          });
        });
      } else if (type == 'events') {
        (array || []).forEach((event: any) => {
          newArray.push({
            id: event.eventId || event.id,
            title: event.eventName || event.name,
            type: 'event',
            url: `events/event-list/view/${event.eventId || event.id}`,
            data: event,
          });
        });
      }
    } else {
      if (type == 'students') {
        (array || []).forEach((item: any) => {
          newArray.push({
            id: item.studentId,
            title: `${item.firstName || ''} ${item.lastName || ''}`,
            type: 'student',
            url: `students/view/${item.studentId}`,
            data: item,
          });
        });
      } else if (type == 'csq_team') {
        (array || []).forEach((item: any) => {
          newArray.push({
            id: item.id,
            title: `${item.firstName || ''} ${item.lastName || ''}`,
            type: 'csq_team',
            url: `csq-team`,
            data: item,
          });
        });
      } else if (type == 'schools') {
        (array || []).forEach((school: any) => {
          newArray.push({
            id: school.schoolId,
            title: school.schoolName,
            type: 'school',
            url: `schools/view/${school.schoolId}`,
            data: school,
          });
        });
      } else if (type == 'users') {
        (array || []).forEach((item: any) => {
          newArray.push({
            id: item.id,
            title: `${item.firstName || ''} ${item.lastName || ''}`,
            url: `users`,
            type: 'user',
            data: item,
          });
        });
      } else if (type == 'events') {
        (array || []).forEach((event: any) => {
          newArray.push({
            id: event.eventId,
            title: event.eventName,
            type: 'event',
            url: `events/event-list/view/${event.eventId}`,
            data: event,
          });
        });
      }
    }

    // }

    this.groupedResults[type == 'students' ? 'student' : type] = newArray;

    this.noResults = false;
  }

  private resetResults(): void {
    this.groupedResults = {
      users: [],
      schools: [],
      events: [],
      csq_team: [],
      student: [],
    };
    this.noResults = false;
    this.isSearching = false;
  }

  toggleSection(section: keyof GroupedResults): void {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  closeSearch(): void {
    this.isOpen = false;
  }

  hasResults(): boolean {
    return !!(
      this.groupedResults?.users?.length ||
      this.groupedResults?.schools?.length ||
      this.groupedResults?.student?.length ||
      this.groupedResults?.csq_team?.length ||
      this.groupedResults?.events?.length
    );
  }

  highlightSearchTerm(text: string): string {
    if (!this.searchText) return text;
    const regex = new RegExp(`(${this.escapeRegExp(this.searchText)})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
  }

  private escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('.global-search-container')) {
      this.closeSearch();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeSearch();
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  }
}
